
import { useState } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function SupportPage() {
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    category: 'general',
    subject: '',
    message: '',
    priority: 'medium'
  });

  const supportCategories = [
    { id: 'general', name: 'General Support', icon: 'ri-question-line' },
    { id: 'technical', name: 'Technical Issues', icon: 'ri-tools-line' },
    { id: 'guidelines', name: 'Guidelines Questions', icon: 'ri-book-line' },
    { id: 'account', name: 'Account & Billing', icon: 'ri-user-settings-line' },
    { id: 'training', name: 'Training & Resources', icon: 'ri-graduation-cap-line' }
  ];

  const faqItems = [
    {
      category: 'general',
      question: 'How often are the guidelines updated?',
      answer: 'Our guidelines are updated in real-time as agencies release new requirements. We monitor all major agencies daily and push updates immediately to ensure you always have the most current information.'
    },
    {
      category: 'general',
      question: 'Can I access guidelines offline?',
      answer: 'Yes! Premium subscribers can download guidelines for offline access. You can also bookmark frequently used guidelines for quick reference.'
    },
    {
      category: 'technical',
      question: 'Why am I having trouble logging in?',
      answer: 'First, ensure you\'re using the correct email and password. If you\'ve forgotten your password, use the "Forgot Password" link. Clear your browser cache if issues persist, or contact support.'
    },
    {
      category: 'technical',
      question: 'The search function isn\'t working properly',
      answer: 'Try clearing your browser cache and cookies. Ensure you\'re using supported browsers (Chrome, Firefox, Safari, Edge). If problems continue, please contact our technical support team.'
    },
    {
      category: 'guidelines',
      question: 'How do I find guidelines for specific property types?',
      answer: 'Use our advanced search with property type filters, or browse by category. You can search for terms like "ADU", "condo", "manufactured home" to find relevant guidelines quickly.'
    },
    {
      category: 'guidelines',
      question: 'Are state-specific requirements included?',
      answer: 'Yes, we include state-specific overlays and requirements where applicable. Use the location filter in search to find guidelines specific to your state or region.'
    },
    {
      category: 'account',
      question: 'How do I upgrade my subscription?',
      answer: 'Go to your account settings and click "Upgrade Plan". You can choose from our Professional or Enterprise plans with additional features and priority support.'
    },
    {
      category: 'account',
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes, you can cancel your subscription at any time from your account settings. You\'ll continue to have access until the end of your current billing period.'
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Support form submitted:', formData);
    // Reset form
    setFormData({
      name: '',
      email: '',
      company: '',
      category: 'general',
      subject: '',
      message: '',
      priority: 'medium'
    });
    alert('Your support request has been submitted. We\'ll get back to you within 24 hours.');
  };

  const filteredFAQs = faqItems.filter(item => item.category === selectedCategory);

  return (
    <div className="min-h-screen bg-slate-900">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Support Center
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Get help when you need it. Our support team is here to assist you with any questions or issues.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Contact Options */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <div className="bg-slate-800 rounded-lg p-6 text-center border border-slate-700">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Phone Support</h3>
              <p className="text-slate-400 mb-4">Speak directly with our support team</p>
              <p className="text-emerald-400 font-semibold">1-800-LOAN-GUIDE</p>
              <p className="text-slate-500 text-sm">Mon-Fri 8AM-8PM EST</p>
            </div>

            <div className="bg-slate-800 rounded-lg p-6 text-center border border-slate-700">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Email Support</h3>
              <p className="text-slate-400 mb-4">Get detailed help via email</p>
              <p className="text-emerald-400 font-semibold">support@loanguide.pro</p>
              <p className="text-slate-500 text-sm">Response within 4 hours</p>
            </div>

            <div className="bg-slate-800 rounded-lg p-6 text-center border border-slate-700">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-chat-3-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Live Chat</h3>
              <p className="text-slate-400 mb-4">Instant help when you need it</p>
              <button className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-500 transition-colors font-medium cursor-pointer whitespace-nowrap">
                Start Chat
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Submit a Support Request</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500 transition-colors"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500 transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500 transition-colors"
                    placeholder="Your company name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">
                      Category *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-emerald-500 transition-colors pr-8"
                    >
                      {supportCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-2">
                      Priority
                    </label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-emerald-500 transition-colors pr-8"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500 transition-colors"
                    placeholder="Brief description of your issue"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500 transition-colors resize-none"
                    placeholder="Please provide detailed information about your issue..."
                  />
                  <p className="text-slate-500 text-sm mt-1">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                <button
                  type="submit"
                  className="w-full bg-emerald-600 text-white py-3 px-6 rounded-lg hover:bg-emerald-500 transition-colors font-medium cursor-pointer whitespace-nowrap"
                >
                  Submit Support Request
                </button>
              </form>
            </div>

            {/* FAQ Section */}
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Frequently Asked Questions</h2>
              
              {/* Category Tabs */}
              <div className="flex flex-wrap gap-2 mb-6">
                {supportCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      selectedCategory === category.id
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    <i className={`${category.icon} mr-2`}></i>
                    {category.name}
                  </button>
                ))}
              </div>

              {/* FAQ Items */}
              <div className="space-y-4">
                {filteredFAQs.map((faq, index) => (
                  <div key={index} className="bg-slate-800 rounded-lg border border-slate-700">
                    <div className="p-4">
                      <h3 className="text-lg font-semibold text-white mb-2">{faq.question}</h3>
                      <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
